<template>
    <Overlay :show="loading">
        <div class="manifest-ui">
            <b-row class="align-items-center mb-2">
                <b-col>
                    <div class="d-flex align-items-center mb-25">
                        <h2 class="mb-0 mr-1">{{ pageTitle }}</h2>
                        <ManifestStatusBadge :status="manifest.status" />
                    </div>
                    <div class="text-muted">
                        {{ importManifestTitle }} manifest
                        <span v-if="latestRevision"> · Latest revision {{ latestRevision }}</span>
                    </div>
                </b-col>

                <b-col class="text-right">
                    <b-button
                        variant="primary"
                        class="mr-1"
                        :disabled="!canSave"
                        @click="saveGeneralInformation"
                    >
                        {{ isNew ? 'Create manifest' : 'Save general information' }}
                    </b-button>
                    <b-button variant="outline-secondary" @click="close">
                        Back
                    </b-button>
                </b-col>
            </b-row>

            <b-alert v-if="isOutdated" variant="secondary" show>
                <div class="alert-body">
                    This manifest is outdated and read-only.
                </div>
            </b-alert>

            <b-tabs v-model="activeTab" pills nav-class="mb-2">
                <b-tab title="General">
                    <ManifestGeneral
                        v-model="form"
                        :disabled="isOutdated"
                    />
                </b-tab>

                <b-tab title="Configuration" :disabled="isNew">
                    <ManifestConfiguration
                        :manifest="manifest"
                        :disabled="isOutdated"
                        @version-created="handleVersionCreated"
                    />
                </b-tab>

                <b-tab title="Versions" :disabled="isNew">
                    <ManifestVersions
                        :manifest-id="manifest.id"
                        :versions="manifest.versions"
                        :active-version-id="manifest.activeVersionId"
                        :disabled="isOutdated"
                        @manifest-updated="handleManifestUpdated"
                    />
                </b-tab>
            </b-tabs>
        </div>
    </Overlay>
</template>

<script>
import {
    BAlert,
    BButton,
    BCol,
    BRow,
    BTab,
    BTabs,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'
import Overlay from '@core/components/common/Overlay.vue'
import backendResponse from '@core/mixins/ui/backend-response'
import ManifestConfiguration from './components/ManifestConfiguration.vue'
import ManifestGeneral from './components/ManifestGeneral.vue'
import ManifestStatusBadge from './components/ManifestStatusBadge.vue'
import ManifestVersions from './components/ManifestVersions.vue'
import {getImportManifestTitle} from './manifestContexts'

const createEmptyManifest = () => ({
    id: null,
    manifestType: 'product_import',
    ownerContext: 'product',
    name: '',
    description: '',
    status: '',
    activeVersionId: null,
    versions: [],
})

export default {
    components: {
        BAlert,
        BButton,
        BCol,
        BRow,
        BTab,
        BTabs,
        ManifestConfiguration,
        ManifestGeneral,
        ManifestStatusBadge,
        ManifestVersions,
        Overlay,
    },
    mixins: [backendResponse],
    props: {
        id: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            activeTab: 0,
            loading: false,
            manifest: createEmptyManifest(),
            form: createEmptyManifest(),
        }
    },
    computed: {
        isNew() {
            return !this.id
        },
        isOutdated() {
            return this.manifest.status === 'outdated'
        },
        pageTitle() {
            return this.isNew ? 'New import manifest' : this.manifest.name || 'Import manifest'
        },
        latestRevision() {
            return this.manifest.versions.reduce(
                (latest, version) => Math.max(latest, version.revision),
                0
            )
        },
        importManifestTitle() {
            return getImportManifestTitle(this.form.manifestType)
        },
        canSave() {
            return !this.loading && !this.isOutdated && this.form.name.trim().length > 0
        },
    },
    watch: {
        id: {
            immediate: true,
            handler() {
                this.initialize()
            },
        },
    },
    methods: {
        initialize() {
            if (!this.id) {
                this.manifest = createEmptyManifest()
                this.form = createEmptyManifest()
                this.activeTab = 0
                return
            }

            this.loading = true
            ImportManifestApi.get(this.id)
                .then(response => {
                    this.setManifest(response.data)
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.loading = false
                })
        },
        setManifest(manifest) {
            this.manifest = {
                ...createEmptyManifest(),
                ...manifest,
                versions: Array.isArray(manifest.versions) ? manifest.versions : [],
            }
            this.form = {
                ...this.manifest,
                versions: [...this.manifest.versions],
            }
        },
        saveGeneralInformation() {
            if (!this.canSave) {
                return
            }

            this.loading = true
            const wasNew = this.isNew
            const request = wasNew
                ? ImportManifestApi.create({
                    manifestType: this.form.manifestType,
                    ownerContext: this.form.ownerContext,
                    name: this.form.name.trim(),
                    description: this.normalizedDescription(),
                })
                : ImportManifestApi.update(this.id, {
                    name: this.form.name.trim(),
                    description: this.normalizedDescription(),
                })

            request
                .then(response => {
                    this.setManifest(response.data)
                    this.showDefaultSuccessMessage(
                        wasNew ? 'Manifest has been created.' : 'Manifest has been updated.'
                    )

                    if (wasNew) {
                        this.activeTab = 1
                        this.$router.replace({
                            name: 'administration-import-product-manifests-manage',
                            params: {id: response.data.id},
                        })
                    }
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.loading = false
                })
        },
        normalizedDescription() {
            const description = (this.form.description || '').trim()
            return description || null
        },
        handleVersionCreated(version) {
            const versions = [
                ...this.manifest.versions.filter(item => item.id !== version.id),
                version,
            ]
            this.manifest = {...this.manifest, versions}
            this.form = {...this.form, versions: [...versions]}
        },
        handleManifestUpdated(manifest) {
            this.setManifest(manifest)
        },
        close() {
            this.$router.push({name: 'administration-import-product-manifests'})
        },
    },
}
</script>

<style lang="scss">
@import './theme';
</style>
